import React from 'react';
import { Row, Col, Card, Statistic, Typography, Progress, Skeleton, Tooltip, Divider, Space } from 'antd';
import { motion } from 'framer-motion';
import { CheckCircleOutlined, InfoCircleOutlined, BankOutlined, SecurityScanOutlined, LineChartOutlined, PieChartOutlined, ThunderboltOutlined, ShareAltOutlined } from '@ant-design/icons';

const { Text } = Typography;

const StatCard = ({ title, value, subtext, icon: Icon, color, progress, loading }) => (
  <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} style={{ height: '100%' }}>
    <Card bordered={false} style={{ borderRadius: 20, boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)', height: '100%' }}>
      {loading ? (
        <Skeleton active paragraph={{ rows: 1 }} />
      ) : (
        <div style={{ display: 'flex', gap: 16 }}>
          <div style={{
            width: 50, height: 50, borderRadius: 12,
            background: `${color}10`, display: 'flex',
            alignItems: 'center', justifyContent: 'center', color: color
          }}>
            <Icon size={24} />
          </div>
          <div style={{ flex: 1 }}>
            <Text strong style={{ color: '#64748b', fontSize: 12, textTransform: 'uppercase' }}>{title}</Text>
            <div style={{ fontSize: 26, fontWeight: 900, color: '#0f172a', lineHeight: 1 }}>{value}</div>
            <Text style={{ fontSize: 11, color: '#94a3b8' }}>{subtext}</Text>
            <div style={{ height: 24, marginTop: 8 }}>
              {progress !== undefined && (
                <Progress percent={progress} size="small" strokeColor={color} style={{ margin: 0 }} />
              )}
            </div>
          </div>
        </div>
      )}
    </Card>
  </motion.div>
);

const OperationStats = ({ stats, loading }) => {
  const {
    total = 0,
    conciliadas = 0,
    pendentes = 0,
    semInformacao = 0,
    percentConciliada = 0,
    percentPendente = 0,
    regimeBreakdown = {},
    segmentoBreakdown = {},
    healthScore = { percent: 0 },
    openFinanceCount = 0,
    pimCount = 0,
    nivelBCount = 0
  } = stats || {};

  const totalEmp = stats?.total || 1;
  const ofPct = Math.round((openFinanceCount / totalEmp) * 100);
  const pimPct = Math.round((pimCount / totalEmp) * 100);
  const nivelBPct = Math.round((nivelBCount / totalEmp) * 100);

  const renderBreakdownTooltip = (data) => (
    <div style={{ padding: '8px' }}>
      {Object.entries(data).map(([key, val]) => (
        <div key={key} style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
          <span>{key}:</span>
          <span style={{ fontWeight: 700 }}>{val} ({Math.round((val / totalEmp) * 100)}%)</span>
        </div>
      ))}
    </div>
  );

  const CustomCard = ({ title, value, subtext, icon: Icon, color, progress, children }) => (
    <Card 
      bordered={false} 
      style={{ 
        borderRadius: 20, 
        height: '100%', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        border: '1px solid #f1f5f9'
      }}
      bodyStyle={{ padding: '20px', display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ background: `${color}10`, padding: 8, borderRadius: 10, display: 'flex', color: color }}>
          <Icon style={{ fontSize: 18 }} />
        </div>
        <Text strong style={{ fontSize: 13, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</Text>
      </div>
      
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 28, fontWeight: 800, color: '#1e293b', marginBottom: 4 }}>{value}</div>
        {children || <Text style={{ fontSize: 12, color: '#94a3b8' }}>{subtext}</Text>}
      </div>

      {progress !== undefined && (
        <div style={{ marginTop: 12 }}>
          <Progress percent={progress} size="small" strokeColor={color} showInfo={false} />
        </div>
      )}
    </Card>
  );

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '20px', marginBottom: 32 }}>
      {/* Saúde Digital */}
      <CustomCard title="Saúde Digital" value={`${healthScore.percent}%`} icon={LineChartOutlined} color="#10b981">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10 }}>
            <span>OF / PIM / B</span>
            <span>{ofPct}% / {pimPct}% / {nivelBPct}%</span>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            <div style={{ flex: 1, height: 4, borderRadius: 2, background: '#3b82f6', opacity: ofPct/100 + 0.2 }} />
            <div style={{ flex: 1, height: 4, borderRadius: 2, background: '#8b5cf6', opacity: pimPct/100 + 0.2 }} />
            <div style={{ flex: 1, height: 4, borderRadius: 2, background: '#10b981', opacity: nivelBPct/100 + 0.2 }} />
          </div>
        </div>
      </CustomCard>

      {/* Total */}
      <Tooltip 
        title={renderBreakdownTooltip(regimeBreakdown)}
        getPopupContainer={trigger => trigger.parentElement}
        mouseEnterDelay={0.3}
        destroyTooltipOnHide
      >
        <div>
          <CustomCard title="Total" value={total} subtext="Empresas Ativas" icon={BankOutlined} color="#2563eb" />
        </div>
      </Tooltip>
      
      {/* Conciliadas */}
      <CustomCard title="Conciliadas" value={conciliadas} subtext="Fechamento OK" icon={CheckCircleOutlined} color="#10b981" progress={percentConciliada} />

      {/* Pendentes */}
      <CustomCard title="Pendentes" value={pendentes} subtext="Ação Necessária" icon={InfoCircleOutlined} color="#f59e0b" progress={percentPendente} />

      {/* Sem Info */}
      <Tooltip 
        title={renderBreakdownTooltip(segmentoBreakdown)}
        getPopupContainer={trigger => trigger.parentElement}
        mouseEnterDelay={0.3}
        destroyTooltipOnHide
      >
        <div>
          <CustomCard title="Sem Info" value={semInformacao} subtext="Risco Crítico" icon={SecurityScanOutlined} color="#ef4444" />
        </div>
      </Tooltip>
    </div>
  );
};

export default OperationStats;

